import { inject, Injectable, signal } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';

import { RoomFile } from './room-file';
import { RoomFilesService } from './room-files.service';

/**
 * Download flow of one view (the files menu or a chat file card): starts the
 * download, asks inline for the password of an encrypted file when the room
 * password does not open it, and reports failures. Provided per component, so
 * each view keeps its own password prompt.
 */
@Injectable()
export class RoomFileDownloadController {
  private readonly _files = inject(RoomFilesService);
  private readonly _snackBar = inject(MatSnackBar);
  private readonly _translate = inject(TranslateService);

  /** The file whose password is being asked for; `wrong` after a failed try. */
  readonly unlocking = signal<{ fileId: string; wrong: boolean } | null>(null);
  readonly password = signal('');

  isUnlocking(file: RoomFile): boolean {
    return this.unlocking()?.fileId === file.id;
  }

  async download(file: RoomFile, password?: string): Promise<void> {
    const result = await this._files.download(file, password);
    switch (result) {
      case 'downloaded':
      case 'cancelled':
        this.cancel();
        break;
      case 'password-required':
        this.unlocking.set({ fileId: file.id, wrong: false });
        break;
      case 'wrong-password':
        this.unlocking.set({ fileId: file.id, wrong: true });
        break;
      case 'gone':
        this.cancel();
        this._notify('"[name]" is no longer available.', file);
        break;
      case 'failed':
        this._notify('"[name]" could not be downloaded.', file);
        break;
    }
  }

  submit(file: RoomFile): void {
    const password = this.password();
    if (password) void this.download(file, password);
  }

  setPassword(event: Event): void {
    this.password.set((event.target as HTMLInputElement).value);
  }

  cancel(): void {
    this.unlocking.set(null);
    this.password.set('');
  }

  private _notify(key: string, file: RoomFile): void {
    this._snackBar.open(this._translate.instant(key, { name: file.name }), undefined, {
      duration: 3000,
      horizontalPosition: 'center',
      verticalPosition: 'top',
    });
  }
}
